/**
 * Celula `Bancă` din grila zilei, editabilă.
 *
 * Cerut de colegele de la contabilitate: „Coloana Banca din Plan de plati sa fie camp editabil. Sa
 * se poata modifica de exemplu INGB."
 *
 * **Banca e o proprietate a FURNIZORULUI, nu a plății.** Codul afișat se deduce din caracterele
 * 5–8 ale IBAN-ului principal, cu revenire la coloana `Banca` din fișier. Deci a-l corecta aici
 * înseamnă a corecta furnizorul, iar corectura se vede pe toate plățile lui, în toate ecranele.
 * Asta e ce vor — eticheta e greșită sau lipsește, și vor s-o repare o dată, nu de N ori.
 *
 * De aceea celula **spune** ce face înainte s-o facă. O editare inline care schimbă tăcut date
 * dincolo de rândul pe care stai e cel mai ușor mod de a pierde încrederea într-o grilă.
 */

import { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';

import { usePatchFurnizor } from '../../hooks/useFurnizori.ts';
import type { DayEntryDto } from '../../hooks/useZi.ts';
import { etichetaBanca } from './filtre.ts';

export interface PropsCelulaBanca {
  intrare: DayEntryDto;
  /** Codurile cunoscute, din `referinta.banks`. Nu e o listă închisă: se acceptă și text liber. */
  banciCunoscute: readonly string[];
  poateScrie: boolean;
}

export function CelulaBanca({ intrare, banciCunoscute, poateScrie }: PropsCelulaBanca) {
  const [editez, setEditez] = useState(false);
  const [text, setText] = useState('');
  const refInput = useRef<HTMLInputElement>(null);
  const patch = usePatchFurnizor();

  useEffect(() => {
    if (editez) refInput.current?.select();
  }, [editez]);

  const afisat = etichetaBanca(intrare);
  const suprascris = intrare.bankCodeOverride !== null && intrare.bankCodeOverride !== undefined;

  function deschide(): void {
    setText(intrare.bankCodeOverride ?? intrare.bankCode ?? '');
    setEditez(true);
  }

  function salveaza(valoare: string | null): void {
    patch.mutate(
      { id: intrare.supplierId, bankCodeOverride: valoare },
      { onSuccess: () => setEditez(false) },
    );
  }

  if (!poateScrie) {
    return (
      <span className="num text-ink-2" title={titlu(intrare, suprascris)}>
        {afisat}
      </span>
    );
  }

  if (!editez) {
    return (
      <button
        type="button"
        className="hit-24 num text-left text-ink-2 hover:underline"
        tabIndex={-1}
        title={titlu(intrare, suprascris)}
        onClick={deschide}
      >
        {afisat}
        {suprascris ? (
          <span className="text-ink-3" aria-hidden="true">
            {' '}
            ✎
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <span className="flex flex-col gap-1">
      <span className="flex items-center gap-1">
        <input
          ref={refInput}
          className="input num w-20 px-1"
          list="banci-cunoscute"
          maxLength={20}
          aria-label={`Banca pentru ${intrare.supplierName}`}
          value={text}
          onChange={(e) => setText(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              salveaza(text.trim() === '' ? null : text.trim());
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setEditez(false);
            }
          }}
        />
        <datalist id="banci-cunoscute">
          {banciCunoscute.map((b) => (
            <option key={b} value={b} />
          ))}
        </datalist>
        <button
          type="button"
          className="hit-24 chip"
          tabIndex={-1}
          aria-label="Salvează banca"
          disabled={patch.isPending}
          onClick={() => salveaza(text.trim() === '' ? null : text.trim())}
        >
          <Check size={12} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="hit-24 chip"
          tabIndex={-1}
          aria-label="Renunță"
          onClick={() => setEditez(false)}
        >
          <X size={12} aria-hidden="true" />
        </button>
      </span>
      {/* Avertismentul e permanent cat timp campul e deschis, nu un tooltip: schimbarea iese din
          randul pe care stai, si asta trebuie sa fie evident INAINTE de Enter, nu dupa. */}
      <span className="text-11 text-ink-3">
        Se schimbă pentru toate plățile lui {intrare.supplierName}. Gol = înapoi la fișier.
      </span>
      {patch.isError ? (
        <span className="text-11 text-neg" role="alert">
          {patch.error.message}
        </span>
      ) : null}
    </span>
  );
}

function titlu(intrare: DayEntryDto, suprascris: boolean): string {
  if (suprascris) {
    return `Corectat manual: ${intrare.bankCodeOverride}. În fișier: ${intrare.bancaRaw ?? '—'}.`;
  }
  return intrare.bancaRaw
    ? `Coloana „Banca” din fișier: ${intrare.bancaRaw}. Click pentru a corecta.`
    : 'Click pentru a corecta banca furnizorului.';
}
